export interface NativeImeViewport { visible: boolean; availableHeightPx: number; webViewHeightPx: number }
export interface KeyboardViewportInput {
  layoutWidth: number;
  layoutHeight: number;
  baselineHeight: number;
  visualHeight?: number | undefined;
  visualTop?: number | undefined;
  scale?: number | undefined;
  editing: boolean;
  nativeIme?: NativeImeViewport | undefined;
}

export function parseNativeImeViewport(value: unknown): NativeImeViewport | undefined {
  if (!value || typeof value !== "object") return undefined;
  const data = value as Partial<NativeImeViewport>;
  return typeof data.visible === "boolean" && typeof data.availableHeightPx === "number" && Number.isFinite(data.availableHeightPx)
    && typeof data.webViewHeightPx === "number" && Number.isFinite(data.webViewHeightPx) && data.webViewHeightPx > 0
    && data.availableHeightPx >= 0 && data.availableHeightPx <= data.webViewHeightPx ? data as NativeImeViewport : undefined;
}

/** A shrinking browser toolbar or pinch zoom is not a software keyboard. */
export function resolveKeyboardViewport(input: KeyboardViewportInput) {
  const height = Math.max(1, Number.isFinite(input.layoutHeight) ? input.layoutHeight : 1);
  const browserHeight = Number.isFinite(input.visualHeight) && input.visualHeight! > 0 ? Math.min(height, input.visualHeight!) : height;
  const native = parseNativeImeViewport(input.nativeIme);
  const nativeHeight = native?.visible ? height * native.availableHeightPx / native.webViewHeightPx : height;
  const visualHeight = Math.max(1, Math.min(browserHeight, nativeHeight));
  const zoomed = Number.isFinite(input.scale) && Math.abs(input.scale! - 1) > .05;
  const occlusion = Math.max(height, input.baselineHeight) - visualHeight;
  return {
    height: zoomed ? height : visualHeight,
    top: zoomed ? 0 : Math.max(0, Math.min(height - visualHeight, input.visualTop ?? 0)),
    keyboardOpen: input.editing && !zoomed && occlusion > Math.max(100, input.baselineHeight * .18),
  };
}

export function focusedTextControl(): HTMLElement | null {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  if (active.isContentEditable || active instanceof HTMLTextAreaElement && !active.disabled && !active.readOnly) return active;
  if (active instanceof HTMLInputElement && !active.disabled && !active.readOnly
    && ["text", "search", "email", "password", "tel", "url", "number"].includes(active.type)) return active;
  return null;
}

/** Scroll only the nearest real scroll containers, and only by the obscured
 * distance. There is no per-keystroke focus, smooth animation or page jump. */
export function revealTextControl(control: HTMLElement, viewportTop: number, viewportHeight: number): void {
  const compose = control.closest<HTMLElement>(".arena-chat-compose, .friend-chat-compose, .lobby-player-row, .party-connect__join>div");
  const target = compose && compose.getBoundingClientRect().height < viewportHeight - 24 ? compose : control;
  for (let parent = control.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
    if (!/(auto|scroll)/.test(getComputedStyle(parent).overflowY) || parent.scrollHeight <= parent.clientHeight + 1) continue;
    const box = target.getBoundingClientRect(); const container = parent.getBoundingClientRect();
    const top = Math.max(viewportTop + 10, container.top + 10);
    const bottom = Math.min(viewportTop + viewportHeight - 10, container.bottom - 10);
    if (box.bottom > bottom) parent.scrollTop += box.bottom - bottom;
    else if (box.top < top) parent.scrollTop -= top - box.top;
  }
}

/** Separate visual viewport dimensions from the arena canvas dimensions. This
 * keeps browser/Android IME overlays above the keyboard without rescaling combat. */
export function initializeKeyboardViewport(): () => void {
  const root = document.documentElement;
  let baselineWidth = window.innerWidth;
  let baselineHeight = window.innerHeight;
  let timer = 0; let frame = 0;
  const update = (reveal = false) => {
    const control = focusedTextControl();
    if (Math.abs(window.innerWidth - baselineWidth) > 80) {
      baselineWidth = window.innerWidth; baselineHeight = window.innerHeight;
    } else if (!control && root.dataset.keyboardOpen !== "true") baselineHeight = window.innerHeight;
    else baselineHeight = Math.max(baselineHeight, window.innerHeight);
    // Keep the occluded layout through input blur: a touch focuses the close/
    // send button before pointerup. Moving that button now would swallow taps.
    const viewport = resolveKeyboardViewport({ layoutWidth: window.innerWidth, layoutHeight: window.innerHeight, baselineHeight, editing: !!control || root.dataset.keyboardOpen === "true",
      visualHeight: window.visualViewport?.height, visualTop: window.visualViewport?.offsetTop, scale: window.visualViewport?.scale,
      nativeIme:parseNativeImeViewport((window as Window & { __varnaxImeViewport?: unknown }).__varnaxImeViewport) });
    root.style.setProperty("--vn-visual-height", `${Math.round(viewport.height)}px`);
    root.style.setProperty("--vn-visual-top", `${Math.round(viewport.top)}px`);
    root.dataset.keyboardOpen = String(viewport.keyboardOpen);
    root.dataset.textEditing = String(!!control);
    if (reveal && control) {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => { if (focusedTextControl() === control) revealTextControl(control, viewport.top, viewport.height); });
    }
  };
  const schedule = () => {
    update();
    window.clearTimeout(timer);
    timer = window.setTimeout(() => update(true), 80);
  };
  const visualScroll = () => update();
  document.addEventListener("focusin", schedule);
  document.addEventListener("focusout", schedule);
  window.addEventListener("resize", schedule);
  window.addEventListener("varnax:ime-viewport", schedule);
  window.visualViewport?.addEventListener("resize", schedule);
  window.visualViewport?.addEventListener("scroll", visualScroll);
  update();
  return () => {
    document.removeEventListener("focusin", schedule); document.removeEventListener("focusout", schedule);
    window.removeEventListener("resize", schedule); window.visualViewport?.removeEventListener("resize", schedule); window.visualViewport?.removeEventListener("scroll", visualScroll);
    window.removeEventListener("varnax:ime-viewport", schedule);
    window.clearTimeout(timer); window.cancelAnimationFrame(frame);
    delete root.dataset.keyboardOpen; delete root.dataset.textEditing;
    root.style.removeProperty("--vn-visual-height"); root.style.removeProperty("--vn-visual-top");
  };
}
